import { useCallback, useState } from "react";
import { useMyPermissions } from "../../lib/permissions.js";
import { denialOrRawMessage } from "../../lib/denialReason.js";
import ConfirmDialog from "../../components/ConfirmDialog.jsx";
import { DIALOG } from "../../lib/analytics/surfaceVocabulary.js";
import {
    createLiteLlmIntegration,
    deleteLiteLlmIntegration,
    renameLiteLlmIntegration,
    rotateLiteLlmCredential,
} from "../../lib/litellmClient.js";
import {
    NO_DIRECTORY,
    litellmIntegration,
    litellmNeedsAttention,
    litellmSteps,
    scopeCaptureStep,
    whenLabel,
} from "./model.js";
import {
    AsideSection,
    CopyBlock,
    ErrorText,
    Field,
    Panel,
    SourceLoadState,
    SourcePage,
    Step,
    Steps,
    Tabs,
    inputCls,
} from "../../components/setupSteps.jsx";
import { TONE, dangerBtnCls, primaryBtnCls, secondaryBtnCls } from "../../components/statusCard.jsx";

const NEW_TAB = "new";
const TOKEN_PLACEHOLDER = "PASTE_YOUR_TOKEN_HERE";

export function callbackConfig(webhookUrl, token) {
    return [
        "callback_settings:",
        "  origin:",
        "    callback_type: generic_api",
        `    endpoint: ${webhookUrl}`,
        "    headers:",
        `      Authorization: Bearer ${token || TOKEN_PLACEHOLDER}`,
        "    log_format: single",
        "    max_retries: 3",
        "",
        "litellm_settings:",
        '  callbacks: ["origin"]',
    ].join("\n");
}

function proxyTone(integration) {
    if (litellmNeedsAttention(integration)) return TONE.ATTENTION;
    return integration.lastDeliveryAt ? TONE.OK : TONE.WAITING;
}

function NameForm({ initial, submitLabel, busyLabel, onSubmit, onCancel }) {
    const caps = useMyPermissions();
    const [name, setName] = useState(initial);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const trimmed = name.trim();

    const submit = useCallback(async () => {
        setSaving(true);
        setError(null);
        try {
            await onSubmit(trimmed);
        } catch (err) {
            setError(denialOrRawMessage(err, caps));
        } finally {
            setSaving(false);
        }
    }, [caps, onSubmit, trimmed]);

    return (
        <Panel className="grid gap-[12px] p-[14px]">
            <Field
                label="Connection name"
                hint="Only shown in Origin. Name it after the LiteLLM deployment it captures, such as Production or Staging."
            >
                <input
                    type="text"
                    value={name}
                    maxLength={100}
                    onChange={(event) => setName(event.target.value)}
                    placeholder="Production"
                    className={inputCls}
                />
            </Field>
            <div className="flex flex-wrap items-center gap-[8px]">
                <button
                    type="button"
                    onClick={submit}
                    disabled={trimmed.length === 0 || trimmed === initial || saving}
                    className={primaryBtnCls}
                >
                    {saving ? busyLabel : submitLabel}
                </button>
                {onCancel ? (
                    <button type="button" onClick={onCancel} className={secondaryBtnCls}>
                        Cancel
                    </button>
                ) : null}
            </div>
            <ErrorText>{error}</ErrorText>
        </Panel>
    );
}

function ConnectSteps({ onCreated }) {
    const create = useCallback(
        async (displayName) => {
            const response = await createLiteLlmIntegration({ displayName });
            await onCreated(response);
        },
        [onCreated],
    );

    return (
        <Steps label="Connect LiteLLM">
            <Step
                number={1}
                state="current"
                title="Name this connection"
                summary="Origin creates a delivery URL and a one-time token for it."
            >
                <NameForm initial="" submitLabel="Create" busyLabel="Creating..." onSubmit={create} />
            </Step>
            <Step
                number={2}
                state="todo"
                title="Add the callback to your LiteLLM config"
                summary="Starts after the connection is created."
            />
            <Step
                number={3}
                state="todo"
                title="Confirm the first call arrives"
                summary="LiteLLM sends each call to Origin a few seconds after it finishes."
                last
            />
        </Steps>
    );
}

const code = "font-mono text-[color:var(--ink)]";

function SetupNotes() {
    return (
        <div className="grid gap-[8px] text-[12px] leading-[1.5] text-[color:var(--muted)]">
            <p className="m-0">
                Already have <span className={code}>litellm_settings</span>? Add <span className={code}>"origin"</span>{" "}
                to its <span className={code}>callbacks</span> instead.
            </p>
            <p className="m-0 font-medium text-[color:var(--ink)]">Optional</p>
            <ul className="m-0 grid list-disc gap-[4px] pl-[18px]">
                <li>
                    Group calls by session: under <span className={code}>litellm_settings</span>, set{" "}
                    <span className={code}>request_correlation_in_logs</span> to <span className={code}>true</span>.
                </li>
                <li>
                    Claude Code: set <span className={code}>ANTHROPIC_BASE_URL</span> to LiteLLM's address, without{" "}
                    <span className={code}>/anthropic</span>.
                </li>
            </ul>
        </div>
    );
}

function ProxySteps({ integration, token, directory, onRotated, onChanged, refreshing }) {
    const caps = useMyPermissions();
    const [renaming, setRenaming] = useState(false);
    const [rotating, setRotating] = useState(false);
    const [error, setError] = useState(null);
    const {
        states: [, configState, deliveryState],
        noDirectory,
    } = scopeCaptureStep(litellmSteps(integration), directory);
    const lastDelivery = whenLabel(integration.lastDeliveryAt);
    const previousUntil = whenLabel(integration.previousCredentialValidUntil);
    const rotateLocked = Boolean(
        integration.previousCredentialValidUntil &&
            new Date(integration.previousCredentialValidUntil).getTime() > Date.now(),
    );

    const rename = useCallback(
        async (displayName) => {
            await renameLiteLlmIntegration(integration.id, displayName);
            setRenaming(false);
            await onChanged();
        },
        [integration.id, onChanged],
    );

    const rotate = useCallback(async () => {
        setRotating(true);
        setError(null);
        try {
            await onRotated(await rotateLiteLlmCredential(integration.id));
        } catch (err) {
            setError(denialOrRawMessage(err, caps));
        } finally {
            setRotating(false);
        }
    }, [caps, integration.id, onRotated]);

    return (
        <Steps label={`LiteLLM connection ${integration.displayName}`}>
            <Step
                number={1}
                state={renaming ? "current" : "done"}
                title="Connection created"
                summary={<>Named {integration.displayName}.</>}
                action={
                    !renaming ? (
                        <button type="button" onClick={() => setRenaming(true)} className={secondaryBtnCls}>
                            Rename
                        </button>
                    ) : null
                }
            >
                {renaming ? (
                    <NameForm
                        initial={integration.displayName}
                        submitLabel="Rename"
                        busyLabel="Renaming..."
                        onSubmit={rename}
                        onCancel={() => setRenaming(false)}
                    />
                ) : null}
            </Step>
            <Step
                number={2}
                state={token ? "current" : configState}
                title="Add Origin to your LiteLLM config"
                summary={
                    token
                        ? "Paste this into your LiteLLM config.yaml, then restart LiteLLM. Copy it now: the token in it won't be shown again."
                        : "Paste this into your LiteLLM config.yaml, then restart LiteLLM."
                }
            >
                {token ? null : (
                    <div className="grid gap-[10px] border border-[color:var(--severity-medium)] p-[12px] sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center">
                        <div className="grid gap-[6px] text-[12px] leading-[1.45] text-[color:var(--ink)]">
                            <p className="m-0">
                                This config has no token. Origin shows a token only once, when it is created. Replace{" "}
                                <span className="font-mono">{TOKEN_PLACEHOLDER}</span> with the token you saved
                                {rotateLocked ? "." : ", or rotate to get a new one."}
                            </p>
                            {rotateLocked ? (
                                <p className="m-0 text-[color:var(--muted)]">
                                    This token was rotated recently, so it can be rotated again after {previousUntil}.
                                    Lost the new token before then? Remove this connection and add a new one.
                                </p>
                            ) : null}
                            <ErrorText>{error}</ErrorText>
                        </div>
                        <button
                            type="button"
                            onClick={rotate}
                            disabled={rotating || rotateLocked}
                            className={secondaryBtnCls}
                        >
                            {rotating ? "Rotating..." : "Rotate token"}
                        </button>
                    </div>
                )}
                <CopyBlock
                    label="config.yaml"
                    value={callbackConfig(integration.webhookUrl, token)}
                    highlight={token ? null : TOKEN_PLACEHOLDER}
                />
                {token && rotateLocked ? (
                    <p className="m-0 text-[12px] leading-[1.4] text-[color:var(--muted)]">
                        The old token works until {previousUntil}.
                    </p>
                ) : null}
                <SetupNotes />
            </Step>
            <Step
                number={3}
                state={deliveryState}
                title={
                    noDirectory
                        ? NO_DIRECTORY.label
                        : deliveryState === "done"
                          ? "Capturing"
                          : "Confirm the first call arrives"
                }
                summary={
                    noDirectory
                        ? NO_DIRECTORY.detail
                        : deliveryState === "failed"
                          ? integration.lastSyncError || "LiteLLM deliveries are failing."
                          : deliveryState === "done"
                            ? `Last delivery ${lastDelivery}.`
                            : "Waiting for the first call."
                }
                action={
                    !noDirectory && deliveryState !== "done" ? (
                        <button
                            type="button"
                            onClick={onChanged}
                            disabled={refreshing}
                            aria-busy={refreshing ? "true" : undefined}
                            className={secondaryBtnCls}
                        >
                            Refresh
                        </button>
                    ) : null
                }
                last
            />
        </Steps>
    );
}

export default function LiteLlmPage({ source, health, entry, directory, onBack, onChanged, selectedItem, onSelectItem }) {
    const caps = useMyPermissions();
    const integrations = entry?.data ?? null;
    const [tokens, setTokens] = useState({});
    const [confirmRemove, setConfirmRemove] = useState(null);
    const [removing, setRemoving] = useState(false);
    const [error, setError] = useState(null);

    const selected =
        integrations && integrations.some((integration) => integration.id === selectedItem)
            ? selectedItem
            : integrations && integrations.length > 0 && selectedItem !== NEW_TAB
              ? integrations[0].id
              : NEW_TAB;
    const current = integrations?.find((integration) => integration.id === selected) ?? null;

    const received = useCallback(
        async (response) => {
            const integration = litellmIntegration(response?.integration);
            const token = response?.webhookToken ?? response?.webhook_token ?? "";
            if (integration.id && token) setTokens((known) => ({ ...known, [integration.id]: token }));
            await onChanged();
            if (integration.id) onSelectItem(integration.id);
        },
        [onChanged, onSelectItem],
    );

    const remove = useCallback(async () => {
        const target = confirmRemove;
        setConfirmRemove(null);
        if (!target) return;
        setRemoving(true);
        setError(null);
        try {
            await deleteLiteLlmIntegration(target.id);
            setTokens(({ [target.id]: _, ...rest }) => rest);
            onSelectItem(null);
            await onChanged();
        } catch (err) {
            setError(denialOrRawMessage(err, caps));
        } finally {
            setRemoving(false);
        }
    }, [caps, confirmRemove, onChanged, onSelectItem]);

    if (!integrations) {
        return (
            <SourcePage source={source} health={health} onBack={onBack} backLabel="Back to data sources" busy={!entry?.loaded || entry?.loading} aside={null}>
                <SourceLoadState entry={entry} onRetry={onChanged} />
            </SourcePage>
        );
    }

    const tabs =
        integrations.length > 0 ? (
            <Tabs
                label="LiteLLM connections"
                selected={selected}
                onSelect={onSelectItem}
                items={[
                    ...integrations.map((integration) => ({
                        id: integration.id,
                        label: integration.displayName,
                        dotTone: proxyTone(integration),
                    })),
                    { id: NEW_TAB, label: "+ Add connection" },
                ]}
            />
        ) : null;

    return (
        <SourcePage
            source={source}
            health={health}
            onBack={onBack}
            backLabel="Back to data sources"
            busy={entry.loading}
            tabs={tabs}
            aside={
                <>
                    <AsideSection title="What Origin does">
                        <p className="m-0">
                            Receives each model call that goes through LiteLLM, with the prompt, the response, and
                            the key that made it.
                        </p>
                    </AsideSection>
                    <AsideSection title="One connection per LiteLLM deployment">
                        <p className="m-0">
                            Add a connection for each LiteLLM deployment you run, such as production and staging.
                            Each gets its own URL and token.
                        </p>
                    </AsideSection>
                    {current ? (
                        <AsideSection title="Remove">
                            <p className="m-0">
                                Origin stops accepting calls from this connection. Prompts already captured are kept.
                            </p>
                            <button
                                type="button"
                                onClick={() => setConfirmRemove(current)}
                                disabled={removing}
                                className={`${dangerBtnCls} w-fit`}
                            >
                                Remove
                            </button>
                            <ErrorText>{error}</ErrorText>
                        </AsideSection>
                    ) : null}
                </>
            }
        >
            {current ? (
                <ProxySteps
                    key={current.id}
                    integration={current}
                    token={tokens[current.id] ?? ""}
                    directory={directory}
                    onRotated={received}
                    onChanged={onChanged}
                    refreshing={entry.loading}
                />
            ) : (
                <ConnectSteps onCreated={received} />
            )}
            <ConfirmDialog
                open={Boolean(confirmRemove)}
                analyticsId={DIALOG.LITELLM_REMOVE}
                eyebrow="LiteLLM"
                title={`Remove ${confirmRemove?.displayName ?? "this connection"}?`}
                body="Origin stops accepting calls from this connection immediately. Prompts already captured are kept. Remove the origin callback from your LiteLLM config too."
                confirmLabel="Remove"
                destructive
                onConfirm={remove}
                onCancel={() => setConfirmRemove(null)}
            />
        </SourcePage>
    );
}
